import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, Mail, Loader2, TrendingUp, Clock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { GOV_DISCLAIMER_ROI } from "../../governance/govData";
import { useGov } from "../../governance/GovContext";
import { savePdf, computeRoi, CURRENCIES, CURRENCY_SYMBOLS } from "../../governance/govPdf";
import { emailRoi } from "../../governance/roiShare";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const DEFAULTS = { volume: 50000, currentSampling: 3, targetCoverage: 100, minutes: 12, cost: 35, currency: "GBP" };

function Field({ label, value, onChange, suffix, testid }) {
  return (
    <div>
      <label className="text-sm text-foreground">{label}</label>
      <div className="mt-1.5 flex items-center gap-2">
        <Input type="number" min={0} value={value} onChange={(e) => onChange(Number(e.target.value))} className="bg-navy-800 border-white/12" data-testid={testid} />
        {suffix && <span className="text-xs text-muted-foreground shrink-0">{suffix}</span>}
      </div>
    </div>
  );
}

function Headline({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rounded-lg border border-primary/25 bg-primary/[0.06] p-5">
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 text-primary"><Icon className="h-4.5 w-4.5" /></span>
      <p className="mt-3 font-heading text-2xl sm:text-3xl font-bold text-foreground tabular-nums break-words" data-testid={testid}>{value}</p>
      <p className="mt-1 text-sm font-medium text-foreground">{label}</p>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export default function RoiCalculator() {
  const { roi, setRoi } = useGov();
  const [v, setV] = useState(roi || DEFAULTS);
  const set = (k, val) => { const n = { ...v, [k]: val }; setV(n); setRoi(n); };

  const [showEmail, setShowEmail] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", consent: false });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const c = useMemo(() => computeRoi(v), [v]);
  const sym = CURRENCY_SYMBOLS[v.currency] || "\u00a3";
  const num = (n) => Math.round(n).toLocaleString("en-GB");
  const money = (n) => sym + num(n);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!form.email || !form.consent) { toast.error("Please add your work email and tick consent."); return; }
    setSending(true);
    try {
      await emailRoi({ ...form, currency: v.currency, inputs: v, results: c });
      setSent(true);
      toast.success("Your business case is on its way — check your inbox.");
    } catch (err) {
      toast.error("Could not send the summary. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="roi-calculator">
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Inputs */}
        <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-foreground">Your conversation estate</p>
            <div className="flex gap-1 rounded-lg border border-white/12 p-0.5" data-testid="roi-currency-group">
              {CURRENCIES.map((cur) => (
                <button key={cur.code} type="button" onClick={() => set("currency", cur.code)}
                  data-testid={`roi-currency-${cur.code.toLowerCase()}`}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${v.currency === cur.code ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                  {cur.symbol} {cur.code}
                </button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Monthly conversation volume" value={v.volume} onChange={(x) => set("volume", x)} testid="roi-volume" />
            <Field label="Currently screened manually" value={v.currentSampling} onChange={(x) => set("currentSampling", x)} suffix="%" testid="roi-current-sampling" />
            <Field label="Target screening coverage" value={v.targetCoverage} onChange={(x) => set("targetCoverage", x)} suffix="%" testid="roi-target-coverage" />
            <Field label="Manual review time each" value={v.minutes} onChange={(x) => set("minutes", x)} suffix="min" testid="roi-minutes" />
            <Field label="Reviewer cost" value={v.cost} onChange={(x) => set("cost", x)} suffix={`${sym}/hr`} testid="roi-cost" />
          </div>
          <p className="text-xs text-muted-foreground/80 pt-1">
            Figures represent the manual-equivalent reviewer effort to screen at your target coverage — the work automated screening can reduce so reviewers focus on flagged interactions.
          </p>
        </div>

        {/* Outputs */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Headline icon={Clock} label="Hours saved / month" value={num(c.monthlyHours)} sub={`${num(c.annualHours)} hrs a year`} testid="roi-out-hours" />
            <Headline icon={TrendingUp} label="Illustrative cost saved / year" value={money(c.annualCost)} sub={`${money(c.monthlyCost)} a month`} testid="roi-out-cost" />
          </div>
          <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Coverage gap closed</p>
            <div className="mt-3 flex items-center gap-3">
              <span className="w-24 shrink-0 text-xs text-muted-foreground">Now · {c.currentSampling}%</span>
              <div className="flex-1 h-3 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full bg-signal-amber" style={{ width: `${Math.min(100, c.currentSampling)}%` }} /></div>
            </div>
            <div className="mt-2 flex items-center gap-3">
              <span className="w-24 shrink-0 text-xs text-muted-foreground">Target · {c.targetCoverage}%</span>
              <div className="flex-1 h-3 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, c.targetCoverage)}%` }} /></div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <Stat label="Screened today" value={`${c.currentReviewed.toLocaleString("en-GB")}/mo`} />
              <Stat label="Additional screened" value={`+${c.extraScreened.toLocaleString("en-GB")}/mo`} />
              <Stat label="Reviewer FTE equivalent" value={`${c.fte.toFixed(1)}`} />
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              You could close a <span className="text-foreground">{c.coverageGapPts.toFixed(0)} percentage-point</span> coverage gap — screening
              <span className="text-foreground"> {c.extraScreened.toLocaleString("en-GB")}</span> more conversations a month.
            </p>
          </div>
          <p className="text-xs text-muted-foreground/80 flex items-start gap-1.5"><ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" /> {GOV_DISCLAIMER_ROI}</p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Button asChild className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="roi-book-cta"><Link to="/book-a-demo">Book a norse3 walkthrough <ArrowRight className="ml-1.5 h-4 w-4" /></Link></Button>
        <Button variant="outline" onClick={() => savePdf.roi(v)} data-testid="roi-download-pdf" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground"><Download className="mr-1.5 h-4 w-4" /> Download business case (PDF)</Button>
        <Button variant="outline" onClick={() => setShowEmail((s) => !s)} data-testid="roi-email-toggle" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground"><Mail className="mr-1.5 h-4 w-4" /> Email me this summary</Button>
      </div>

      {showEmail && !sent && (
        <form onSubmit={submitEmail} className="rounded-xl border border-primary/25 bg-primary/[0.06] p-4 grid gap-3 sm:grid-cols-2" data-testid="roi-email-form">
          <Input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-navy-800 border-white/12" data-testid="roi-email-name" />
          <Input placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-navy-800 border-white/12" data-testid="roi-email-company" />
          <Input type="email" required placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-navy-800 border-white/12 sm:col-span-2" data-testid="roi-email-address" />
          <label className="sm:col-span-2 flex items-start gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5" data-testid="roi-email-consent" />
            <span>I agree to receive this business-case summary and for norse3 to contact me about it. Illustrative only — not a guarantee of savings.</span>
          </label>
          <div className="sm:col-span-2">
            <Button type="submit" size="sm" disabled={sending || !form.consent || !form.email} data-testid="roi-email-submit" className="bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Mail className="mr-1.5 h-4 w-4" />} Send me the summary
            </Button>
          </div>
        </form>
      )}

      {sent && (
        <p className="text-sm text-signal-green" data-testid="roi-email-sent">Sent — check your inbox for your ROI &amp; value summary.</p>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3"><p className="font-heading text-lg font-bold text-foreground">{value}</p><p className="text-[0.7rem] text-muted-foreground mt-0.5">{label}</p></div>;
}
